// The upload tray: every file put here in this tab, from the moment it is picked until it is secured
// or has failed, and the files of an import from Google Drive, which are importing until they have
// arrived and are then encrypted like any upload. Encrypting is its own step rather than part of the progress bar, because it is the
// part done after the bytes have left — the bar measures only what the browser can see. A file
// going into Locked is sealing rather than encrypting, and sealed in Locked once it is done, in
// violet.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useState} from 'react'

import CheckIcon from '@mui/icons-material/CheckRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import CollapseIcon from '@mui/icons-material/RemoveRounded'
import ExpandIcon from '@mui/icons-material/AddRounded'
import FailedIcon from '@mui/icons-material/ErrorOutlineRounded'
import FileIcon from '@mui/icons-material/InsertDriveFileOutlined'
import IconButton from '@mui/material/IconButton'

import {SETTLED_PHASES} from '../../lib/hooks/useUploads'
import {formatBytes} from '../../lib/fileTools'

import './uploadTray.scss'

const FAILED_PHASES = ['blocked', 'failed']

const STATUS = {
  queued: () => 'Queued',
  uploading: ({progress}) => `Uploading ${progress}%`,
  waiting: () => 'Waiting to encrypt',
  importing: () => 'Importing…',
  scanning: () => 'Scanning…',
  encrypting: () => 'Encrypting…',
  done: () => 'Uploaded',
  blocked: () => 'Blocked by the virus scan',
  failed: () => 'Failed'
}

// What a file going into Locked says in place of the drive's words.
const LOCKED_STATUS = {
  waiting: () => 'Waiting to seal',
  encrypting: () => 'Sealing…',
  done: () => 'Sealed in Locked'
}

const statusOf = upload => ((upload.locked && LOCKED_STATUS[upload.phase]) || STATUS[upload.phase])(upload)

// Only the browser's part is measured. Before it there is nothing to show, and after it the bar
// stays full while the worker has the file, since the worker reports steps rather than amounts.
const fillWidth = ({phase, progress}) => {
  if (phase === 'queued') return 0

  return phase === 'uploading' ? progress : 100
}

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

const trayTitle = uploads => {
  const active = uploads.filter(({phase}) => !SETTLED_PHASES.includes(phase)).length
  const failed = uploads.filter(({phase}) => FAILED_PHASES.includes(phase)).length

  if (active) return `Uploading ${plural(active, 'item')}`
  if (!failed) return `${plural(uploads.length, 'upload')} complete`
  if (failed === uploads.length) return `${plural(failed, 'upload')} failed`

  return `${uploads.length - failed} complete · ${failed} failed`
}

const uploadShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  // Unknown for a Google document until it has been exported.
  size: PropTypes.number,
  phase: PropTypes.oneOf(Object.keys(STATUS)).isRequired,
  progress: PropTypes.number.isRequired,
  locked: PropTypes.bool
})

const TrayRow = ({upload}) => {
  const {name, size, phase} = upload
  const failed = FAILED_PHASES.includes(phase)
  const settled = SETTLED_PHASES.includes(phase)

  return (
    <li className={classnames('tray-row', {failed, locked: upload.locked})}>
      <FileIcon className='tray-glyph' />
      <div className='tray-body'>
        <div className='tray-name'>{name}</div>
        {!settled && (
          <div className='tray-track'>
            <span className='tray-fill' style={{width: `${fillWidth(upload)}%`}} />
          </div>
        )}
        <div className='tray-status'>{statusOf(upload)} · {formatBytes(size)}</div>
      </div>
      {phase === 'done' && <CheckIcon className='tray-outcome' />}
      {failed && <FailedIcon className='tray-outcome' />}
    </li>
  )
}

TrayRow.propTypes = {upload: uploadShape.isRequired}

const UploadTray = ({uploads, onDismiss}) => {
  const [collapsed, setCollapsed] = useState(false)

  if (!uploads.length) return null

  return (
    <section className={classnames('upload-tray', {collapsed})} aria-label='Uploads'>
      <div className='tray-head'>
        <span className='tray-title' role='status'>{trayTitle(uploads)}</span>
        <IconButton
          className='tray-button'
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} uploads`}
          onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ExpandIcon /> : <CollapseIcon />}
        </IconButton>
        <IconButton className='tray-button' aria-label='Dismiss uploads' onClick={onDismiss}>
          <CloseIcon />
        </IconButton>
      </div>
      {!collapsed && <ul className='tray-list'>{uploads.map(upload => <TrayRow key={upload.key} upload={upload} />)}</ul>}
    </section>
  )
}

UploadTray.propTypes = {
  uploads: PropTypes.arrayOf(uploadShape).isRequired,
  onDismiss: PropTypes.func.isRequired
}

export default UploadTray
